import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { walletApi } from '../api/wallet';
import { authApi } from '../api/auth';
import { usersApi } from '../api/users';
import { Wallet, ArrowDown, ArrowUp, Info } from 'lucide-react';
import { refreshBalance } from '../hooks/useBalance';
import { formatD10S } from '../utils/formatters';
import { useWallet } from '../context/WalletContext';
import { balanceManager } from '../context/walletcontext/balanceManager';
import { getCurrentNetwork } from '../config/blockchain';
import { useLanguage } from '../i18n/LanguageContext';
import { useConfirmSpendD10S } from '../context/ConfirmD10SContext';
import '../styles/deposit-gaming.css';

const getAssociatedWallet = (u) => (u?.wallet_address || u?.walletAddress || '').trim() || null;

/** Su Android costruisce un Intent URL per provare ad aprire l'app Wise con il link di pagamento. */
function getWiseAppIntentUrl(webPaymentLink) {
  if (!webPaymentLink || typeof webPaymentLink !== 'string') return webPaymentLink;
  try {
    const url = new URL(webPaymentLink);
    const pathAndQuery = url.pathname + url.search;
    const intentPath = '//' + url.host + pathAndQuery;
    return 'intent:' + intentPath + '#Intent;package=com.transferwise.android;scheme=https;S.browser_fallback_url=' + encodeURIComponent(webPaymentLink) + ';end';
  } catch (_) {
    return webPaymentLink;
  }
}

const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

/** Solo crypto attivo: Stripe deposito e prelievo EUR (fiat) disabilitati ma visibili. */
const STRIPE_DEPOSIT_UI_DISABLED = true;
const PAYPAL_DEPOSIT_UI_DISABLED = true;
const FIAT_WITHDRAW_UI_DISABLED = true;
const PAYPAL_FRIENDS_FAMILY_EMAIL = 'sidibi.sd@gmail.com';

/** Dati per bonifico ordinario e ricarica Postepay (accredito manuale). */
const BONIFICO_ORDINARIO = {
  iban: 'IT81R0760117200001075461630',
  intestatario: 'Dettori Uilliam'
};

const RICARICA_POSTEPAY = {
  numeroCarta: '5333173555474180',
  scadenza: '05/30',
  codiceFiscale: 'DTTLLM76H13A192Z'
};

function buildPaypalDirectUrl({ email, amount, code }) {
  if (!email || !amount || amount <= 0) return null;

  const params = new URLSearchParams({
    cmd: '_xclick',
    business: email,
    currency_code: 'EUR',
    amount: amount.toFixed(2),
    item_name: `DIOS PayPal Deposit - ${code || 'pending'}`,
    custom: code || '',
    no_shipping: '1'
  });

  return `https://www.paypal.com/cgi-bin/webscr?${params.toString()}`;
}

// Form interno per pagamento con carta (usa useStripe/useElements, va dentro <Elements>)
const StripeCardForm = ({ clientSecret, amount, onSuccess, onError, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    onError(null);
    try {
      const cardElement = elements.getElement(CardElement);
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement }
      });
      if (confirmError) {
        onError(confirmError.message || 'Pagamento fallito');
        return;
      }
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess();
      } else {
        onError('Pagamento non completato');
      }
    } catch (err) {
      onError(err?.message || 'Errore durante il pagamento');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 rounded-xl bg-gray-800/80 border border-gray-600">
        <CardElement
          options={{
            style: {
              base: { color: '#e5e7eb', fontSize: '16px', '::placeholder': { color: '#9ca3af' } },
              invalid: { color: '#f87171' }
            }
          }}
        />
      </div>
      <div className="flex gap-3">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-xl border border-gray-500 text-gray-300 hover:bg-gray-700">
          Annulla
        </button>
        <button type="submit" disabled={!stripe || submitting} className="flex-1 btn-football py-3 disabled:opacity-50">
          {submitting ? 'Pagamento in corso...' : `Paga ${amount} EUR`}
        </button>
      </div>
    </form>
  );
};

const Deposit = () => {
  const { t } = useLanguage();
  const [user] = useState(authApi.getCurrentUser());
  const [activeTab, setActiveTab] = useState('deposit'); // deposit, withdraw
  const [balance, setBalance] = useState(null);
  const [canWithdraw, setCanWithdraw] = useState(null);
  // eslint-disable-next-line no-unused-vars -- setLoading usato in loadBalance/loadWithdrawStatus
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    loadBalance();
    loadWithdrawStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadBalance = async () => {
    const depositUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;
    if (!depositUserId) return;
    setLoading(true);
    try {
      const response = await walletApi.getBalance(depositUserId, { force: true });
      if (response?.success) {
        setBalance(response.balance);
        }
      } catch (error) {
      console.error('Error loading balance:', error);
      } finally {
      setLoading(false);
    }
  };

  const loadWithdrawStatus = async () => {
    const depositUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;
    if (!depositUserId) return;
    try {
      const response = await walletApi.canWithdraw(depositUserId);
      if (response?.success) {
        setCanWithdraw(response);
      }
    } catch (error) {
      console.error('Error loading withdraw status:', error);
    }
  };

  return (
    <div className="deposit-container">
      {/* Deposit Background */}
      <div 
        className="deposit-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/homebg.png')`
        }}
      ></div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <h1 className="text-4xl font-bold mb-8 flex items-center">
          <Wallet className="w-10 h-10 mr-3" />
          Wallet
        </h1>

        {/* Balance Card */}
        {balance && (
          <div className="scoreboard p-8 mb-6">
            <div className="text-center">
              <div className="mb-2">Your Balance</div>
              <div className="text-5xl font-bold mb-4">
                {formatD10S(balance.d10s || 0)} DIOS
              </div>
              {(parseFloat(balance.bonus) || 0) > 0 && (
                <div className="text-amber-300 text-lg font-semibold mb-2">
                  + {formatD10S(balance.bonus)} Bonus
                  <span className="block text-xs font-normal text-amber-200/80 mt-1">
                    Non prelevabile · solo aste e pack
                  </span>
                </div>
              )}
              <div className="text-sm">
                1 DIOS = 1€
              </div>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div>
                  <div className="text-sm">Total Deposited</div>
                  <div className="font-semibold">{balance.totalDeposited || 0} DIOS</div>
                </div>
                <div>
                  <div className="text-sm">Total Spent</div>
                  <div className="font-semibold">{balance.totalSpent || 0} DIOS</div>
                </div>
              </div>
              {balance.canWithdraw === false && (
                <div className="mt-4 p-4 bg-yellow-900/50 border border-yellow-500">
                  <div className="flex items-center space-x-2">
                    <Info className="w-5 h-5" />
                    <span className="text-sm">{balance.withdrawReason || 'You need to spend at least 1x your deposit before withdrawing'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => setActiveTab('deposit')}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-all ${
              activeTab === 'deposit'
                ? 'bg-yellow-400 text-black'
                : ''
            }`}
          >
            <ArrowDown className="w-5 h-5" />
            <span>{t('activityReport.category.deposit')}</span>
          </button>
          <button
            onClick={() => setActiveTab('withdraw')}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-all ${
              activeTab === 'withdraw'
                ? 'bg-yellow-400 text-black'
                : ''
            }`}
          >
            <ArrowUp className="w-5 h-5" />
            <span>{t('activityReport.category.withdrawal')}</span>
          </button>
        </div>

        {/* Content */}
        {activeTab === 'deposit' && <DepositTab user={user} onDeposit={loadBalance} />}
        {activeTab === 'withdraw' && <WithdrawTab user={user} canWithdraw={canWithdraw} onWithdraw={loadBalance} />}
      </div>
    </div>
  );
};

const PROMO_SUPPORTED_METHODS = new Set(['USDT', 'USDC', 'Stripe', 'PayPal', 'Wise']);

const DepositPromoBlock = ({ promoCode, onPromoCodeChange, preview, loading }) => (
  <div className="mt-3 p-4 rounded-xl bg-amber-900/15 border border-amber-500/40">
    <label className="block mb-2 text-sm font-semibold text-amber-200">PROMO CODE</label>
    <input
      type="text"
      value={promoCode}
      onChange={(e) => onPromoCodeChange(e.target.value.toUpperCase())}
      className="w-full px-4 py-2 rounded-xl bg-gray-800 border border-gray-600 text-gray-100 font-mono uppercase"
      placeholder="Inserisci codice promo"
      autoComplete="off"
    />
    {loading && <p className="mt-2 text-xs text-gray-400">Verifica codice...</p>}
    {!loading && preview?.valid && (
      <p className="mt-2 text-sm text-amber-300">
        Bonus <strong>+{preview.bonusPercent}%</strong>
        {preview.estimatedBonus != null && (
          <> → <strong>+{Number(preview.estimatedBonus).toFixed(2)} Bonus</strong> (solo aste e pack)</>
        )}
      </p>
    )}
    {!loading && preview && !preview.valid && promoCode.trim() && (
      <p className="mt-2 text-sm text-red-400">{preview.error}</p>
    )}
  </div>
);

const DepositTab = ({ user, onDeposit }) => {
  const { t, locale } = useLanguage();
  const { confirm } = useConfirmSpendD10S();
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'USDT'
  });
  const [promoCode, setPromoCode] = useState('');
  const [promoPreview, setPromoPreview] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [waitingForConfirmation, setWaitingForConfirmation] = useState(false);
  const [eurUsdRate, setEurUsdRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(true);
  const [depositMethod, setDepositMethod] = useState('USDT'); // 'USDT' | 'USDC' | 'Stripe' | 'PayPal' | 'Bonifico' | 'Postepay'
  const [wiseResult, setWiseResult] = useState(null); // { reference, iban, bic, beneficiary, amount, expiresAt }
  const [wiseLinkCopied, setWiseLinkCopied] = useState(false);
  const [manualCopyKey, setManualCopyKey] = useState(null);
  const [paypalDepositRequest, setPaypalDepositRequest] = useState(null);
  const [paypalCodeCopied, setPaypalCodeCopied] = useState(false);
  const [creatingPaypalDeposit, setCreatingPaypalDeposit] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [stripeAmount, setStripeAmount] = useState(null);
  const [stripeSuccess, setStripeSuccess] = useState(false);
  const [stripePublishableKey, setStripePublishableKey] = useState(() =>
    (typeof process !== 'undefined' && process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) || ''
  );
  const [loadingStripeConfig, setLoadingStripeConfig] = useState(false);
  const stripePromise = useMemo(
    () => (stripePublishableKey ? loadStripe(stripePublishableKey) : null),
    [stripePublishableKey]
  );

  // Se la chiave Stripe non c'è in .env, la prendiamo dal backend (una sola volta per tab Stripe, no loop)
  const STRIPE_CONFIG_TIMEOUT_MS = 10000;
  useEffect(() => {
    if (STRIPE_DEPOSIT_UI_DISABLED && depositMethod === 'Stripe') {
      setDepositMethod('USDT');
      return;
    }
    if (PAYPAL_DEPOSIT_UI_DISABLED && depositMethod === 'PayPal') {
      setDepositMethod('USDT');
    }
  }, [depositMethod]);

  useEffect(() => {
    if (depositMethod !== 'Stripe' || stripePublishableKey || STRIPE_DEPOSIT_UI_DISABLED) return;
    let cancelled = false;
    setLoadingStripeConfig(true);
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), STRIPE_CONFIG_TIMEOUT_MS)
    );
    Promise.race([walletApi.getEurDepositConfig(), timeoutPromise])
      .then((res) => {
        if (!cancelled && res?.stripePublishableKey) setStripePublishableKey(res.stripePublishableKey);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingStripeConfig(false);
      });
    return () => { cancelled = true; };
  }, [depositMethod, stripePublishableKey]);

  const {
    account,
    signer,
    currentNetwork,
    ensureSigner,
    switchToBSC,
    chainId
  } = useWallet();
  const associatedWallet = getAssociatedWallet(user);

  // Dynamic EUR/USD rate for USDT → DIOS (1 EUR = 1 DIOS)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await walletApi.getEurUsdRate();
        if (!cancelled && res?.success && res.eurUsdRate != null) {
          setEurUsdRate(res.eurUsdRate);
        }
      } catch (e) {
        if (!cancelled) setEurUsdRate(1.08);
      } finally {
        if (!cancelled) setRateLoading(false);
      }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const usdtAmount = parseFloat(formData.amount) || 0;
  const estimatedD10s = eurUsdRate && eurUsdRate > 0 ? usdtAmount / eurUsdRate : null;

  const depositD10sForPromo = useMemo(() => {
    const amount = parseFloat(formData.amount);
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    if (depositMethod === 'USDT' || depositMethod === 'USDC') {
      return estimatedD10s != null ? estimatedD10s : 0;
    }
    if (PROMO_SUPPORTED_METHODS.has(depositMethod) && depositMethod !== 'USDT' && depositMethod !== 'USDC') {
      return amount;
    }
    return 0;
  }, [formData.amount, depositMethod, estimatedD10s]);

  const supportsPromo = PROMO_SUPPORTED_METHODS.has(depositMethod);
  const depositUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;

  useEffect(() => {
    if (!supportsPromo || !depositUserId) {
      setPromoPreview(null);
      return;
    }
    const code = promoCode.trim();
    if (!code || depositD10sForPromo <= 0) {
      setPromoPreview(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPromoLoading(true);
      try {
        const res = await walletApi.previewPromoCode(code, depositD10sForPromo);
        if (!cancelled) setPromoPreview(res?.preview || null);
      } catch (err) {
        if (!cancelled) {
          setPromoPreview({
            valid: false,
            error: err?.response?.data?.error || err?.message || 'Invalid promo code'
          });
        }
      } finally {
        if (!cancelled) setPromoLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [promoCode, depositD10sForPromo, supportsPromo, depositUserId]);

  const paypalAmount = parseFloat(formData.amount);
  const paypalAmountValid = Number.isFinite(paypalAmount) && paypalAmount >= 5 && paypalAmount <= 10000;
  const paypalPaymentUrl = useMemo(
    () => buildPaypalDirectUrl({
      email: PAYPAL_FRIENDS_FAMILY_EMAIL,
      amount: paypalDepositRequest?.amount ? Number(paypalDepositRequest.amount) : null,
      code: paypalDepositRequest?.code || ''
    }),
    [paypalDepositRequest]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!depositUserId) return;

    setError(null);
    setResult(null);

    const amount = parseFloat(formData.amount);
    const isCrypto = depositMethod === 'USDT' || depositMethod === 'USDC';
    if (!isCrypto) return;
    if (!amount || amount <= 0) {
      setError(depositMethod === 'USDC' ? 'Enter a valid USDC amount.' : 'Enter a valid USDT amount.');
      return;
    }

    if (!associatedWallet) {
      setError('Associate your wallet first in Profile > Settings. Only the linked wallet can deposit.');
      return;
    }

    const amountToConfirm = (eurUsdRate && eurUsdRate > 0 ? amount / eurUsdRate : amount);
    const ok = await confirm(amountToConfirm, t('confirmD10S.actionDeposit'));
    if (!ok) return;

    setLoading(true);
    try {
      const net = getCurrentNetwork();
      const networkParam = net.chainId === 56 ? 'mainnet' : 'testnet';

      const depositInfo = await walletApi.getUsdtAddress(networkParam);
      if (!depositInfo?.success || !depositInfo?.targetWallet) {
        setError('Could not load deposit address. Try again.');
        return;
      }
      if (depositMethod === 'USDC' && !depositInfo?.usdcAddress) {
        setError('USDC deposit is not available on this network. Use mainnet or USDT.');
        return;
      }

      let activeSigner = signer;
      if (!activeSigner) {
        activeSigner = await ensureSigner();
      }
      if (!activeSigner) {
        setError(`To send ${depositMethod}, connect your wallet in Profile > Settings first, then return here to deposit.`);
        return;
      }

      const connectedAddress = (account || (await activeSigner.getAddress())).toLowerCase();
      if (connectedAddress !== associatedWallet.toLowerCase()) {
        setError('The connected wallet does not match the one linked to this account. Deposit and withdrawal are only allowed from the wallet linked in Profile > Settings. Please connect the correct wallet.');
        return;
      }

      const targetWallet = depositInfo.targetWallet;
      const networkToUse = currentNetwork || net;
      const expectedChainId = net.chainId;
      if (chainId != null && chainId !== expectedChainId && switchToBSC) {
        try {
          await switchToBSC();
        } catch (switchErr) {
          setError('Please switch to BSC network in your wallet.');
          return;
        }
      }

      const tx = depositMethod === 'USDC'
        ? await balanceManager.sendUSDCTransaction(activeSigner, networkToUse, targetWallet, amount)
        : await balanceManager.sendUSDTTransaction(activeSigner, networkToUse, targetWallet, amount);
      const transactionHash = tx?.hash;
      if (!transactionHash) {
        setError('Transaction sent but hash not received. Check your wallet.');
        return;
      }

      setWaitingForConfirmation(true);
      let receipt;
      try {
        receipt = await tx.wait();
      } catch (waitErr) {
        setWaitingForConfirmation(false);
        setError('Transaction failed or was dropped. Check your wallet or try again.');
        return;
      }
      if (!receipt || receipt.status !== 1) {
        setWaitingForConfirmation(false);
        setError('Transaction failed on-chain. No DIOS have been credited.');
        return;
      }

      // Re-read amount so payload is explicit after long tx.wait(); backend requires amount + currency
      const amountToSend = typeof amount === 'number' && amount > 0 ? amount : parseFloat(formData.amount) || 0;
      if (!amountToSend) {
        setError('Could not determine deposit amount. Please try again with a valid amount.');
        return;
      }
      const response = await walletApi.deposit({
        amount: amountToSend,
        currency: depositMethod,
        transactionHash,
        promoCode: promoCode.trim() || undefined
      });

      if (response?.success) {
        setWaitingForConfirmation(false);
        setResult(response);
        setFormData({ amount: '', currency: depositMethod });
        onDeposit();
        refreshBalance(depositUserId);
      } else {
        setWaitingForConfirmation(false);
        setError(response?.message || 'Deposit registration failed.');
      }
    } catch (err) {
      setWaitingForConfirmation(false);
      const msg = err?.message || 'Deposit failed';
      if (msg.includes('user rejected') || msg.includes('User denied')) {
        setError('Transaction cancelled.');
      } else if (msg.includes('insufficient') || msg.includes('balance')) {
        setError(`Insufficient ${depositMethod} balance in your wallet.`);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
      setWaitingForConfirmation(false);
    }
  };

  const handleWiseInitiate = async (e) => {
    e.preventDefault();
    if (!depositUserId) return;
    const amount = parseFloat(formData.amount);
    if (!amount || amount < 5) {
      setError('Minimum Wise deposit is 5 EUR.');
      return;
    }
    if (amount > 10000) {
      setError('Maximum Wise deposit is 10,000 EUR.');
      return;
    }
    setError(null);
    setWiseResult(null);
    setLoading(true);
    try {
      const response = await walletApi.initiateWiseDeposit(depositUserId, amount, promoCode);
      if (response?.success && response?.wiseDeposit) {
        setWiseResult(response.wiseDeposit);
        setFormData({ ...formData, amount: '' });
      } else {
        setError(response?.error || response?.message || 'Could not create Wise deposit.');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Wise deposit initiation failed.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaypalInitiate = async () => {
    if (!depositUserId) return;
    if (!paypalAmountValid) {
      setError('Inserisci un importo PayPal valido tra 5 e 10.000 EUR.');
      return;
    }

    setCreatingPaypalDeposit(true);
    setError(null);
    try {
      const response = await walletApi.initiatePaypalDeposit(depositUserId, paypalAmount, promoCode);
      if (response?.success && response?.paypalDeposit) {
        setPaypalDepositRequest(response.paypalDeposit);
        setPaypalCodeCopied(false);
      } else {
        setError(response?.error || response?.message || 'Impossibile creare il codice PayPal.');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Errore durante la creazione del codice PayPal.');
    } finally {
      setCreatingPaypalDeposit(false);
    }
  };

  const copyManual = (text, key) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setManualCopyKey(key);
      setTimeout(() => setManualCopyKey(null), 2500);
    }
  };

  const stripeKey = stripePublishableKey;
  const stripePromiseInstance = stripePromise;

  return (
    <div className="football-card glass-football p-8">
      <h2 className="text-2xl font-bold mb-6">{t('depositPage.title')}</h2>

      {/* Payment method: USDT, USDC, Stripe (card), PayPal */}
      <p className="text-sm text-gray-400 mb-3">{t('depositPage.chooseMethod')}</p>
      <div className="mb-6 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => {
            setDepositMethod('USDT');
            setError(null);
            setResult(null);
            setWiseResult(null);
            setPaypalDepositRequest(null);
            setPaypalCodeCopied(false);
            setManualCopyKey(null);
          }}
          className={`px-5 py-3 rounded-xl font-semibold border-2 transition-all ${depositMethod === 'USDT' ? 'bg-yellow-600 border-yellow-500 text-white' : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'}`}
        >
          USDT (crypto)
        </button>
        <button
          type="button"
          onClick={() => {
            setDepositMethod('USDC');
            setError(null);
            setResult(null);
            setWiseResult(null);
            setPaypalDepositRequest(null);
            setPaypalCodeCopied(false);
            setManualCopyKey(null);
          }}
          className={`px-5 py-3 rounded-xl font-semibold border-2 transition-all ${depositMethod === 'USDC' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'}`}
        >
          USDC (crypto)
        </button>
        <button
          type="button"
          onClick={() => {
            setDepositMethod('Bonifico');
            setError(null);
            setResult(null);
            setWiseResult(null);
            setPaypalDepositRequest(null);
            setPaypalCodeCopied(false);
            setStripeClientSecret(null);
            setStripeSuccess(false);
            setManualCopyKey(null);
          }}
          className={`px-5 py-3 rounded-xl font-semibold border-2 transition-all ${
            depositMethod === 'Bonifico'
              ? 'bg-emerald-600 border-emerald-500 text-white'
              : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'
          }`}
        >
          {t('depositPage.methodBonifico')}
        </button>
        <button
          type="button"
          onClick={() => {
            setDepositMethod('Postepay');
            setError(null);
            setResult(null);
            setWiseResult(null);
            setPaypalDepositRequest(null);
            setPaypalCodeCopied(false);
            setStripeClientSecret(null);
            setStripeSuccess(false);
            setManualCopyKey(null);
          }}
          className={`px-5 py-3 rounded-xl font-semibold border-2 transition-all ${
            depositMethod === 'Postepay'
              ? 'bg-amber-600 border-amber-500 text-white'
              : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'
          }`}
        >
          {t('depositPage.methodPostepay')}
        </button>
        {!PAYPAL_DEPOSIT_UI_DISABLED && (
          <button
            type="button"
            onClick={() => {
              setDepositMethod('PayPal');
              setError(null);
              setResult(null);
              setWiseResult(null);
              setStripeClientSecret(null);
              setStripeSuccess(false);
              setPaypalDepositRequest(null);
              setPaypalCodeCopied(false);
              setManualCopyKey(null);
            }}
            className={`px-5 py-3 rounded-xl font-semibold border-2 transition-all ${
              depositMethod === 'PayPal'
                ? 'bg-sky-600 border-sky-500 text-white'
                : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'
            }`}
          >
            PayPal
          </button>
        )}
        <button
          type="button"
          disabled={STRIPE_DEPOSIT_UI_DISABLED}
          title={STRIPE_DEPOSIT_UI_DISABLED ? t('depositPage.stripeUnavailableTitle') : undefined}
          onClick={() => {
            if (STRIPE_DEPOSIT_UI_DISABLED) return;
            setDepositMethod('Stripe');
            setError(null);
            setResult(null);
            setWiseResult(null);
            setStripeClientSecret(null);
            setStripeSuccess(false);
            setPaypalDepositRequest(null);
            setPaypalCodeCopied(false);
            setManualCopyKey(null);
          }}
          className={`px-5 py-3 rounded-xl font-semibold border-2 transition-all ${
            STRIPE_DEPOSIT_UI_DISABLED
              ? 'bg-gray-900/80 border-gray-700 text-gray-500 opacity-60 cursor-not-allowed'
              : depositMethod === 'Stripe'
                ? 'bg-purple-600 border-purple-500 text-white'
                : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'
          }`}
        >
          Stripe (carta)
        </button>
      </div>

      {depositMethod === 'PayPal' && !PAYPAL_DEPOSIT_UI_DISABLED && (
        <div className="p-4 bg-sky-900/20 border border-sky-500/50 rounded-xl space-y-4">
          <p className="font-medium text-sky-200">Pagamento PayPal</p>
          <p className="text-sm text-gray-300">
            Genera prima un codice univoco, poi apri PayPal con importo gia compilato e inserisci quel codice nella nota della transazione.
          </p>

          <div>
            <label className="block mb-2">Importo da inviare (EUR)</label>
            <input
              type="number"
              step="0.01"
              min="5"
              max="10000"
              value={formData.amount}
              onChange={(e) => {
                setFormData({ ...formData, amount: e.target.value });
                setPaypalDepositRequest(null);
                setPaypalCodeCopied(false);
                setError(null);
              }}
              className="w-full px-4 py-2 rounded-xl bg-gray-800 border border-gray-600 text-gray-100"
              placeholder="es. 50"
            />
            <p className="mt-2 text-sm text-gray-400">1 EUR = 1 DIOS. Min 5 EUR, max 10.000 EUR.</p>
            <DepositPromoBlock
              promoCode={promoCode}
              onPromoCodeChange={setPromoCode}
              preview={promoPreview}
              loading={promoLoading}
            />
          </div>

          {!paypalDepositRequest && (
            <button
              type="button"
              onClick={handlePaypalInitiate}
              disabled={creatingPaypalDeposit || !paypalAmountValid}
              className="inline-flex items-center justify-center w-full py-3 px-4 bg-sky-600 hover:bg-sky-500 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
            >
              {creatingPaypalDeposit
                ? 'Creazione codice in corso...'
                : `Genera codice PayPal${paypalAmountValid ? ` ${paypalAmount.toFixed(2)} EUR` : ''}`}
            </button>
          )}

          {paypalDepositRequest && (
            <div className="space-y-3">
              <div className="p-4 bg-gray-900/60 border border-sky-500/40 rounded-xl space-y-3">
                <p className="text-sm text-gray-300">Codice PayPal da inserire nella nota del pagamento:</p>
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-mono text-lg text-white break-all">{paypalDepositRequest.code}</p>
                    <p className="text-xs text-gray-400 mt-1">Importo registrato: {Number(paypalDepositRequest.amount).toFixed(2)} EUR</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
                        navigator.clipboard.writeText(paypalDepositRequest.code);
                        setPaypalCodeCopied(true);
                        setTimeout(() => setPaypalCodeCopied(false), 2500);
                      }
                    }}
                    className="inline-flex items-center justify-center py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-100 text-sm font-medium rounded-xl transition-colors"
                  >
                    {paypalCodeCopied ? 'Codice copiato ✓' : 'Copia codice'}
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (!paypalPaymentUrl) {
                    setError('Impossibile aprire PayPal. Rigenera il codice.');
                    return;
                  }
                  setError(null);
                  window.open(paypalPaymentUrl, '_blank', 'noopener,noreferrer');
                }}
                className="inline-flex items-center justify-center w-full py-3 px-4 bg-sky-600 hover:bg-sky-500 text-white font-semibold rounded-xl transition-colors"
              >
                {`Paga con PayPal ${Number(paypalDepositRequest.amount).toFixed(2)} EUR`}
              </button>
              <p className="text-xs text-gray-400">
                Si aprira PayPal con l'importo gia inserito. Prima di confermare scegli <strong>Amici e familiari</strong> e incolla il codice nella nota.
              </p>
            </div>
          )}

          <div className="p-4 bg-red-900/20 border border-red-500/40 rounded-xl text-sm text-gray-300">
            L'accredito PayPal non e automatico come Stripe: verra verificato manualmente dopo il pagamento.
          </div>
        </div>
      )}

      {depositMethod === 'Stripe' && !STRIPE_DEPOSIT_UI_DISABLED && (
        <div className="p-4 bg-purple-900/20 border border-purple-500/50 rounded-xl space-y-4">
          <p className="font-medium text-purple-200">Pagamento con carta (Stripe)</p>
          <p className="text-sm text-gray-300">
            Deposita in <strong>EUR</strong> con carta. 1 EUR = 1 DIOS. Min 5 EUR, max 10.000 EUR.
          </p>
          {loadingStripeConfig && (
            <p className="text-gray-400 text-sm">Caricamento configurazione Stripe...</p>
          )}
          {!loadingStripeConfig && !stripeKey && (
            <p className="text-amber-400 text-sm">Stripe non è disponibile. In produzione verifica che il backend sia raggiungibile (REACT_APP_BACKEND_URL), che CORS sia configurato e che nel backend ci siano STRIPE_PUBLISHABLE_KEY e ENABLE_STRIPE=true. Oppure usa USDT o USDC.</p>
          )}
          {stripeKey && !stripeSuccess && !stripeClientSecret && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const depositUserId = user?.linkedUserId ?? user?.id;
                if (!depositUserId) return;
                const amount = parseFloat(formData.amount);
                if (!amount || amount < 5) { setError('Minimo 5 EUR'); return; }
                if (amount > 10000) { setError('Massimo 10.000 EUR'); return; }
                setError(null);
                setLoading(true);
                try {
                  const response = await walletApi.initiateEurDeposit(depositUserId, amount, promoCode);
                  if (response?.success && response?.depositRequest?.clientSecret) {
                    setStripeClientSecret(response.depositRequest.clientSecret);
                    setStripeAmount(amount);
                  } else {
                    setError(response?.error || 'Impossibile avviare il pagamento.');
                  }
                } catch (err) {
                  setError(err?.data?.error || err?.message || 'Errore di connessione.');
                } finally {
                  setLoading(false);
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="block mb-2">Importo (EUR)</label>
                <input
                  type="number"
                  step="0.01"
                  min="5"
                  max="10000"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl bg-gray-800 border border-gray-600 text-gray-100"
                  placeholder="es. 50"
                  required
                />
                <DepositPromoBlock
                  promoCode={promoCode}
                  onPromoCodeChange={setPromoCode}
                  preview={promoPreview}
                  loading={promoLoading}
                />
              </div>
              <button type="submit" disabled={loading || !formData.amount} className="btn-football w-full py-3 disabled:opacity-50">
                {loading ? 'Caricamento...' : 'Paga con carta'}
              </button>
            </form>
          )}
          {stripeKey && stripeClientSecret && !stripeSuccess && (
            <div className="space-y-3">
              <p className="text-sm text-gray-300">Inserisci i dati della carta per pagare <strong>{stripeAmount} EUR</strong>.</p>
              <Elements stripe={stripePromiseInstance} options={{ clientSecret: stripeClientSecret }}>
                <StripeCardForm
                  clientSecret={stripeClientSecret}
                  amount={stripeAmount}
                  onSuccess={() => {
                    setStripeSuccess(true);
                    setStripeClientSecret(null);
                    setStripeAmount(null);
                    setFormData((prev) => ({ ...prev, amount: '' }));
                    if (typeof onDeposit === 'function') onDeposit();
                    const depositUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;
                    refreshBalance(depositUserId);
                  }}
                  onError={(msg) => setError(msg)}
                  onCancel={() => { setStripeClientSecret(null); setStripeAmount(null); setError(null); }}
                />
              </Elements>
            </div>
          )}
          {stripeSuccess && (
            <div className="p-4 bg-green-900/40 border border-green-500 rounded-xl">
              <p className="font-semibold text-green-400">Pagamento completato</p>
              <p className="text-sm text-gray-300 mt-1">Il saldo verrà aggiornato a breve. Se non si aggiorna, contatta il supporto.</p>
            </div>
          )}
        </div>
      )}

      {depositMethod === 'Bonifico' && (
        <div className="p-4 bg-emerald-900/20 border border-emerald-500/50 rounded-xl space-y-4">
          <p className="font-medium text-emerald-200">{t('depositPage.bonificoTitle')}</p>
          <p className="text-sm text-gray-300">
            {t('depositPage.bonificoLead')}
            <strong>{t('depositPage.usernameEmphasis')}</strong>
            {t('depositPage.bonificoTail')}
          </p>
          <div className="space-y-3">
            <div className="p-3 bg-gray-900/60 border border-emerald-500/30 rounded-xl flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs text-gray-400">{t('depositPage.labelIban')}</p>
                <p className="font-mono text-white break-all">{BONIFICO_ORDINARIO.iban}</p>
              </div>
              <button
                type="button"
                onClick={() => copyManual(BONIFICO_ORDINARIO.iban, 'bonifico-iban')}
                className="shrink-0 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-100 text-sm font-medium rounded-xl transition-colors"
              >
                {manualCopyKey === 'bonifico-iban' ? t('depositPage.copied') : t('depositPage.copyIban')}
              </button>
            </div>
            <div className="p-3 bg-gray-900/60 border border-emerald-500/30 rounded-xl flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs text-gray-400">{t('depositPage.labelAccountHolder')}</p>
                <p className="font-mono text-white">{BONIFICO_ORDINARIO.intestatario}</p>
              </div>
              <button
                type="button"
                onClick={() => copyManual(BONIFICO_ORDINARIO.intestatario, 'bonifico-intestatario')}
                className="shrink-0 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-100 text-sm font-medium rounded-xl transition-colors"
              >
                {manualCopyKey === 'bonifico-intestatario' ? t('depositPage.copied') : t('depositPage.copy')}
              </button>
            </div>
          </div>
        </div>
      )}

      {depositMethod === 'Postepay' && (
        <div className="p-4 bg-amber-900/20 border border-amber-500/50 rounded-xl space-y-4">
          <p className="font-medium text-amber-200">{t('depositPage.postepayTitle')}</p>
          <p className="text-sm text-gray-300">
            {t('depositPage.postepayLead')}
            <strong>{t('depositPage.postepayCausaleLabel')}</strong>
            {t('depositPage.postepayMid')}
            <strong>{t('depositPage.usernameEmphasis')}</strong>
            {t('depositPage.postepayTail')}
          </p>
          <div className="space-y-3">
            <div className="p-3 bg-gray-900/60 border border-amber-500/30 rounded-xl flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs text-gray-400">{t('depositPage.labelCardNumber')}</p>
                <p className="font-mono text-white break-all">{RICARICA_POSTEPAY.numeroCarta}</p>
              </div>
              <button
                type="button"
                onClick={() => copyManual(RICARICA_POSTEPAY.numeroCarta, 'postepay-carta')}
                className="shrink-0 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-100 text-sm font-medium rounded-xl transition-colors"
              >
                {manualCopyKey === 'postepay-carta' ? t('depositPage.copied') : t('depositPage.copyNumber')}
              </button>
            </div>
            <div className="p-3 bg-gray-900/60 border border-amber-500/30 rounded-xl flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs text-gray-400">{t('depositPage.labelExpiry')}</p>
                <p className="font-mono text-white">{RICARICA_POSTEPAY.scadenza}</p>
              </div>
              <button
                type="button"
                onClick={() => copyManual(RICARICA_POSTEPAY.scadenza, 'postepay-scad')}
                className="shrink-0 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-100 text-sm font-medium rounded-xl transition-colors"
              >
                {manualCopyKey === 'postepay-scad' ? t('depositPage.copied') : t('depositPage.copy')}
              </button>
            </div>
            <div className="p-3 bg-gray-900/60 border border-amber-500/30 rounded-xl flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs text-gray-400">{t('depositPage.labelFiscalCode')}</p>
                <p className="font-mono text-white break-all uppercase tracking-wide">{RICARICA_POSTEPAY.codiceFiscale}</p>
              </div>
              <button
                type="button"
                onClick={() => copyManual(RICARICA_POSTEPAY.codiceFiscale, 'postepay-cf')}
                className="shrink-0 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-100 text-sm font-medium rounded-xl transition-colors"
              >
                {manualCopyKey === 'postepay-cf' ? t('depositPage.copied') : t('depositPage.copyFiscalCode')}
              </button>
            </div>
          </div>
        </div>
      )}

      {depositMethod === 'Wise' && (
        <>
          <p className="mb-4 text-gray-300">
            Deposit in <strong>EUR</strong> via bank transfer to our Wise account. 1 EUR = 1 DIOS. Put the <strong>reference</strong> in your transfer so we can credit your account.
          </p>
          {!wiseResult ? (
            <form onSubmit={handleWiseInitiate} className="space-y-6">
              <div>
                <label className="block mb-2">Amount (EUR)</label>
                <input
                  type="number"
                  step="0.01"
                  min="5"
                  max="10000"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full"
                  placeholder="e.g. 50"
                  required
                />
                <p className="mt-1 text-sm text-gray-400">Min 5 EUR, max 10,000 EUR. You will receive the same amount in DIOS.</p>
                <DepositPromoBlock
                  promoCode={promoCode}
                  onPromoCodeChange={setPromoCode}
                  preview={promoPreview}
                  loading={promoLoading}
                />
              </div>
              <button type="submit" disabled={loading || !formData.amount} className="btn-football w-full text-lg py-3 disabled:opacity-50">
                {loading ? 'Loading...' : 'Get transfer details'}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              {wiseResult.paymentLink && (
                <div className="p-4 bg-green-900/40 border border-green-500 rounded-xl space-y-3">
                  <p className="font-semibold text-green-400 mb-2">Paga aprendo Wise</p>
                  <p className="text-sm text-gray-300">Clicca il pulsante: si aprirà Wise (sito o app) con importo e causale. Completa il pagamento lì.</p>
                  <p className="text-xs text-gray-400">
                    {isAndroid
                      ? 'Su Android il pulsante qui sotto prova ad aprire l’app Wise. Se si apre il browser, usa il menu (⋮) → «Apri con l’app» oppure «Copia link».'
                      : 'Su smartphone il link può aprirsi nel browser: usa il menu (⋮) → «Apri con l’app» oppure copia il link e incollalo nell’app Wise.'}
                  </p>
                  <div className="flex flex-col gap-2">
                    <a
                      href={isAndroid ? getWiseAppIntentUrl(wiseResult.paymentLink) : wiseResult.paymentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-full py-3 px-4 bg-green-600 hover:bg-green-500 text-white font-semibold rounded-xl transition-colors"
                    >
                      {isAndroid ? 'Apri nell’app Wise e paga' : 'Apri Wise e paga'} {wiseResult.amount} EUR
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
                          navigator.clipboard.writeText(wiseResult.paymentLink);
                          setWiseLinkCopied(true);
                          setTimeout(() => setWiseLinkCopied(false), 2500);
                        }
                      }}
                      className="inline-flex items-center justify-center w-full py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-medium rounded-xl transition-colors"
                    >
                      {wiseLinkCopied ? 'Link copiato ✓' : 'Copia link (poi incolla nell’app Wise)'}
                    </button>
                  </div>
                </div>
              )}
              <div className="p-4 bg-green-900/30 border border-green-500/50 rounded-lg">
                <p className="font-semibold text-green-400 mb-2">Oppure bonifico manuale (IBAN):</p>
                <div className="space-y-1 text-sm font-mono break-all">
                  <p><span className="text-gray-400">Beneficiary:</span> {wiseResult.beneficiary}</p>
                  <p><span className="text-gray-400">IBAN:</span> {wiseResult.iban}</p>
                  {wiseResult.bic && <p><span className="text-gray-400">BIC/SWIFT:</span> {wiseResult.bic}</p>}
                  <p><span className="text-gray-400">Amount:</span> {wiseResult.amount} EUR</p>
                  <p className="mt-2 pt-2 border-t border-gray-600"><span className="text-yellow-400 font-bold">Reference (obbligatorio):</span> {wiseResult.reference}</p>
                </div>
                <p className="mt-3 text-xs text-gray-400">Valid until {new Date(wiseResult.expiresAt).toLocaleString(locale || 'it-IT')}. After transferring, your balance will be updated automatically (usually within 1–2 business days).</p>
              </div>
              <button type="button" onClick={() => setWiseResult(null)} className="text-sm text-gray-400 hover:text-white">
                Create another Wise deposit
              </button>
            </div>
          )}
          {error && depositMethod === 'Wise' && (
            <div className="mt-4 bg-red-900/50 border border-red-500 p-4 rounded"><p>{error}</p></div>
          )}
        </>
      )}

      {(depositMethod === 'USDT' || depositMethod === 'USDC') && (
        <>
      <p className="mb-6">
        Deposit with <strong>{depositMethod}</strong> (BEP-20). Amount is converted to DIOS using the current EUR/USD rate (1 EUR = 1 DIOS).
      </p>

      {/* Dynamic exchange rate display */}
      <div className={`mb-6 p-4 bg-black/30 rounded-lg border ${depositMethod === 'USDC' ? 'border-blue-500/50' : 'border-yellow-500/50'}`}>
        <div className={`text-sm font-semibold mb-1 ${depositMethod === 'USDC' ? 'text-blue-400' : 'text-yellow-400'}`}>Exchange rate</div>
        {rateLoading ? (
          <span className="text-sm">Loading rate…</span>
        ) : (
          <div className="text-sm space-y-0.5">
            <div>1 EUR = 1 DIOS</div>
            <div>EUR/USD: <strong>{eurUsdRate != null ? Number(eurUsdRate).toFixed(4) : '—'}</strong> <span className="opacity-75">(used for {depositMethod} → DIOS)</span></div>
            <div>1 {depositMethod} ≈ <strong>{eurUsdRate > 0 ? (1 / eurUsdRate).toFixed(4) : '—'}</strong> DIOS</div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block mb-2">Amount ({depositMethod})</label>
                <input
                  type="number"
                  step="0.01"
            min="0.01"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            className="w-full"
            placeholder={depositMethod === 'USDC' ? 'Enter USDC amount' : 'Enter USDT amount'}
            required
          />
          {estimatedD10s != null && formData.amount.trim() !== '' && (
            <p className="mt-1 text-sm text-gray-400">≈ {estimatedD10s.toFixed(2)} DIOS</p>
          )}
          <DepositPromoBlock
            promoCode={promoCode}
            onPromoCodeChange={setPromoCode}
            preview={promoPreview}
            loading={promoLoading}
          />
            </div>

            <button
          type="submit"
          disabled={loading || !formData.amount || (promoCode.trim() && promoPreview && !promoPreview.valid)}
          className="btn-football w-full text-lg py-3 disabled:opacity-50"
        >
          {loading ? 'Confirm in MetaMask...' : `Deposit ${depositMethod}`}
            </button>

        {waitingForConfirmation && (
          <div className="deposit-waiting-confirmation">
            <p className="font-semibold">Transaction submitted. Waiting for confirmation…</p>
            <p className="text-sm opacity-90 mt-1">Do not close or refresh this page.</p>
          </div>
        )}

        {error && (
          <div className="bg-red-900/50 border border-red-500 p-4">
            <p>{error}</p>
              </div>
            )}

        {result && (
          <div className="bg-green-900/50 border border-green-500 p-4">
            <p>Deposit successful! Your balance will be updated shortly.</p>
          </div>
        )}
      </form>
        </>
      )}

      <div className="mt-6 p-4 bg-blue-900/50 border border-blue-500">
        <div className="flex items-start space-x-2">
          <Info className="w-5 h-5 mt-0.5" />
                <div className="text-sm">
            <p className="font-semibold mb-1">Important:</p>
            <p>To withdraw funds, you must spend at least 1x your total deposit amount. This ensures a 1:1 payroll ratio.</p>
                </div>
              </div>
            </div>
    </div>
  );
};

const WithdrawTab = ({ user, canWithdraw, onWithdraw }) => {
  const { t } = useLanguage();
  const { confirm } = useConfirmSpendD10S();
  const associatedWalletWithdraw = getAssociatedWallet(user);
  const [formData, setFormData] = useState({
    amount: '',
    currency: FIAT_WITHDRAW_UI_DISABLED ? 'USDT' : 'EUR',
    address: '',
    skrillEmail: '',
    wiseEmail: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [eurUsdRateWithdraw, setEurUsdRateWithdraw] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await walletApi.getEurUsdRate();
        if (!cancelled && res?.success && res.eurUsdRate != null) {
          setEurUsdRateWithdraw(Number(res.eurUsdRate));
        }
      } catch (_) {
        if (!cancelled) setEurUsdRateWithdraw(1.08);
      }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const isFiat = formData.currency === 'EUR';
  const isCrypto = !isFiat;

  React.useEffect(() => {
    if (FIAT_WITHDRAW_UI_DISABLED && formData.currency === 'EUR') {
      setFormData((prev) => ({ ...prev, currency: 'USDT' }));
    }
  }, [formData.currency]);

  // When user selects crypto, pre-fill and lock address to the wallet linked to the account
  React.useEffect(() => {
    if (isCrypto && associatedWalletWithdraw) {
      setFormData((prev) => ({ ...prev, address: associatedWalletWithdraw }));
    } else if (isCrypto && !associatedWalletWithdraw) {
      setFormData((prev) => ({ ...prev, address: '' }));
    }
  }, [formData.currency, associatedWalletWithdraw, isCrypto]);

  const { account } = useWallet();

  const withdrawUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!withdrawUserId) return;

    if (isFiat && !formData.skrillEmail.trim()) {
      setError('Insert Skrill email for EUR withdrawals.');
      return;
    }

    if (!isFiat) {
      if (!associatedWalletWithdraw) {
        setError('To withdraw in crypto you must link a wallet in Profile > Settings first.');
        return;
      }
      if (formData.address.trim().toLowerCase() !== associatedWalletWithdraw.toLowerCase()) {
        setError('Crypto withdrawals are only sent to the wallet linked to your account.');
        return;
      }
    }

    if (!isFiat && associatedWalletWithdraw) {
      const connectedAddr = (account || '').toLowerCase();
      if (!connectedAddr || connectedAddr !== associatedWalletWithdraw.toLowerCase()) {
        setError('Withdrawals are only allowed when the connected wallet is the one linked to this account. Connect the linked wallet in Profile > Settings.');
        return;
      }
    }

    const withdrawAmount = parseFloat(formData.amount);
    const ok = await confirm(withdrawAmount, t('confirmD10S.actionWithdraw'));
    if (!ok) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      if (isFiat) {
        if (formData.skrillEmail.trim()) {
          await usersApi.updateSkrillEmail(withdrawUserId, formData.skrillEmail.trim());
        }
        const storedUser = authApi.getCurrentUser();
        if (storedUser && formData.skrillEmail.trim()) {
          const updated = { ...storedUser, skrill_email: formData.skrillEmail.trim() };
          localStorage.setItem('user', JSON.stringify(updated));
        }
      }

      const response = await walletApi.withdraw({
        userId: withdrawUserId,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        address: isFiat ? null : (associatedWalletWithdraw || formData.address).trim()
      });

      if (response?.success) {
        setResult(response);
        setFormData({
          amount: '',
          currency: FIAT_WITHDRAW_UI_DISABLED ? 'USDT' : 'EUR',
          address: '',
          skrillEmail: '',
          wiseEmail: ''
        });
        onWithdraw();
        refreshBalance(withdrawUserId);
      }
    } catch (err) {
      setError(err.message || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="football-card glass-football p-8">
      <h2 className="text-2xl font-bold mb-6">Withdraw Funds</h2>

      {(parseFloat(canWithdraw?.bonusBalance) || 0) > 0 && (
        <p className="mb-4 text-sm text-amber-300/90">
          Il saldo Bonus ({formatD10S(canWithdraw.bonusBalance)}) non è prelevabile: i prelievi usano solo DIOS.
        </p>
      )}

      {canWithdraw?.canWithdraw === false && (
        <div className="mb-6 p-4 bg-red-900/50 border border-red-500">
          <div className="flex items-start space-x-2">
            <Info className="w-5 h-5 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold mb-1">Withdrawal Not Available</p>
              <p>{canWithdraw.reason || 'You need to spend at least 1x your total deposit before withdrawing.'}</p>
                  </div>
              </div>
              </div>
            )}

      <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
          <label className="block mb-2">Amount (DIOS)</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            className="w-full"
            placeholder="Enter amount"
            required
            disabled={!canWithdraw?.canWithdraw}
          />
          {isCrypto && eurUsdRateWithdraw != null && Number.parseFloat(formData.amount) > 0 && (
            <p className="text-sm text-gray-300 mt-2">
              ≈{' '}
              <strong className="text-white">
                {(Number.parseFloat(formData.amount) * eurUsdRateWithdraw).toFixed(2)} {formData.currency}
              </strong>
              {' '}
              (1 DIOS = 1 EUR, converted to USD at EUR/USD {Number(eurUsdRateWithdraw).toFixed(4)} — same rate as USDT deposits)
            </p>
          )}
          {isFiat && Number.parseFloat(formData.amount) > 0 && (
            <p className="text-sm text-gray-300 mt-2">
              ≈ <strong className="text-white">{Number.parseFloat(formData.amount).toFixed(2)} EUR</strong> via Skrill (1 DIOS = 1 EUR)
            </p>
          )}
        </div>

        <div>
          <label className="block mb-2">Currency</label>
          <select
            value={formData.currency}
            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
            className="w-full"
            disabled={!canWithdraw?.canWithdraw}
          >
            <option value="EUR" disabled={FIAT_WITHDRAW_UI_DISABLED}>
              {FIAT_WITHDRAW_UI_DISABLED ? 'EUR (Euro) — non disponibile' : 'EUR (Euro)'}
            </option>
            <option value="USDT">USDT</option>
            <option value="USDC">USDC</option>
          </select>
            </div>
            
        {isFiat ? (
          <>
            <div>
              <label className="block mb-2">Skrill Email</label>
              <input
                type="email"
                value={formData.skrillEmail}
                onChange={(e) => setFormData({ ...formData, skrillEmail: e.target.value })}
                className="w-full"
                placeholder="name@example.com"
                disabled={!canWithdraw?.canWithdraw}
              />
            </div>
            <p className="text-sm text-gray-400">Inserisci l&apos;email Skrill per prelievi in EUR.</p>
          </>
        ) : (
          <div>
            <label className="block mb-2">Withdrawal Address</label>
            <input
              type="text"
              value={associatedWalletWithdraw || formData.address}
              readOnly
              className="w-full bg-gray-700 cursor-not-allowed"
              placeholder={associatedWalletWithdraw ? undefined : 'Link a wallet in Profile > Settings'}
              disabled={!canWithdraw?.canWithdraw || !associatedWalletWithdraw}
              title={associatedWalletWithdraw ? 'Crypto withdrawals are only sent to the wallet linked to your account.' : 'Link a wallet in Profile > Settings to withdraw in crypto.'}
            />
            {!associatedWalletWithdraw && (
              <p className="text-amber-400 text-sm mt-1">Link a wallet in Profile → Settings to withdraw in crypto.</p>
            )}
          </div>
        )}
            
        <button
          type="submit"
          disabled={loading || !formData.amount || !canWithdraw?.canWithdraw || (isCrypto && !associatedWalletWithdraw)}
          className="btn-football w-full text-lg py-3 disabled:opacity-50"
        >
          {loading ? 'Processing...' : 'Withdraw'}
        </button>

        {error && (
          <div className="bg-red-900/50 border border-red-500 p-4">
            <p>{error}</p>
            </div>
        )}

        {result && (
          <div className="bg-green-900/50 border border-green-500 p-4">
            <p>Withdrawal request submitted! It will be processed shortly.</p>
          </div>
        )}
      </form>
    </div>
  );
};

export default Deposit;

